import _ from 'underscore';
import Promise from 'bluebird';
import { useCallback, useContext } from 'react';
import { useWizardService } from '@bingads-webui-react/wizard';
import { loadCampaign, saveBudget, loadAdGroupForBudget, modelToViewModelForBudget, readScheduleCriterions } from '@bingads-webui-campaign-react/search-campaign-data';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { mediumEnum } from '@bingads-webui-campaign-react/constants';
import { ScopeContext } from '@bingads-webui-react/scope-service';

const defaultCampaignSettings = {
  BiddingScheme: {
    Type: 'EnhancedCpc',
  },
};

const defaultAdGroup = {
  AdDistribution: 'BingYahooAndSearchPartners',
};

export const useBudgetAndBidSettingsRepository = () => {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const scenario = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);
  const { cid, aid } = scope.values;
  const { state } = useWizardService();
  const { campaignId, adGroupId } = state.context.wizardData;

  const load = useCallback(() => {
    const loadPromises = {
      campaign: campaignId ? loadCampaign(cid, aid, campaignId) : Promise.resolve(defaultCampaignSettings),
      adGroup: adGroupId ? loadAdGroupForBudget(cid, aid, campaignId, adGroupId, i18n) : Promise.resolve(defaultAdGroup),
      scheduleCriterion: campaignId ? readScheduleCriterions(cid, aid, campaignId) : Promise.resolve(null),
    };

    return Promise.props(loadPromises)
      .then(({
        campaign: rawCampaign,
        adGroup,
        scheduleCriterion,
      }) => {
        const campaign = modelToViewModelForBudget({
          campaignModel: rawCampaign,
          scheduleCriterionModel: scheduleCriterion ? scheduleCriterion.value : null,
        });

        return Promise.resolve({
          campaign,
          adGroup: _.defaults(adGroup, {
            Medium: mediumEnum.Search, // default to Search for saarch campaign
          }),
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(`Load budget and bid settings failed: ${error}`);
        activity.signal(`onBidBudgetLoadFailed - ${JSON.stringify({
          campaignId,
          adGroupId,
          error,
        })}`);
        return Promise.reject(error);
      });
  }, [activity, adGroupId, aid, campaignId, cid, i18n]);

  const save = useCallback(({ data }) => {
    // to do later: bulk
    const saveBudgetPromise = () => saveBudget(data, {
      scenario: scenario.name,
      i18n,
      activityLogger: activity,
    });

    return saveBudgetPromise()
      .catch((error) => {
        const {
          serverErrors = [],
          systemErrors = [],
        } = error || {};

        // eslint-disable-next-line no-console
        console.error(`Save budget and bid settings failed: ${error}`);
        activity.signal(`onBidBudgetSaveFailed - ${JSON.stringify({
          data,
          error,
        })}`);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          data: {
            campaign: error.campaign,
            adGroup: error.adGroup,
          },
          error: {
            serverErrors: _.flatten(serverErrors.filter(val => val !== null)),
            systemErrors: _.flatten(systemErrors.filter(val => val !== null)),
          },
        });
      });
  }, [activity, i18n, scenario.name]);

  return {
    load,
    save,
  };
};
